/**
 * Currency-aware money formatters.
 * Defaults come from configureMoney() / useCurrency(); callers can override code/decimals.
 */

const STORAGE_KEY = "pos_currency_cfg";

function readStoredCurrency() {
  if (typeof window === "undefined") return { code: "AED", decimals: 2 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.code) {
        const code = String(parsed.code).toUpperCase();
        const decimals = Number.isFinite(Number(parsed.decimals))
          ? Number(parsed.decimals)
          : (code === "OMR" ? 3 : 2);
        return { code, decimals };
      }
    }
  } catch (_) {}
  return { code: "AED", decimals: 2 };
}

const initialConfig = readStoredCurrency();
let _code = initialConfig.code;
let _decimals = initialConfig.decimals;

export function configureMoney({ code, decimals } = {}) {
  if (code) {
    _code = String(code).toUpperCase();
    if (decimals == null) {
      _decimals = decimalsForCurrency(_code);
    }
  }
  if (decimals != null && Number.isFinite(Number(decimals))) {
    _decimals = Math.max(0, Math.min(4, Number(decimals)));
  }
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: _code, decimals: _decimals }));
    } catch (_) {}
  }
}

export function currentCurrency() {
  return { code: _code, decimals: _decimals };
}

export function decimalsForCurrency(code) {
  const c = String(code || _code || "AED").toUpperCase();
  return c === "OMR" ? 3 : 2;
}

function resolveDecimals(code, decimals) {
  if (decimals != null && Number.isFinite(Number(decimals))) return Number(decimals);
  if (code) return decimalsForCurrency(code);
  return _decimals;
}

function resolveCode(code) {
  return String(code || _code || "AED").toUpperCase();
}

/** Numeric string with correct fraction digits (no currency symbol). */
export const money2 = (n, code, decimals) => {
  const dp = resolveDecimals(code, decimals);
  const factor = 10 ** dp;
  const rounded = Math.round((Number(n) || 0) * factor) / factor;
  return rounded.toFixed(dp);
};

/** Amount + ISO code (never locale dirham glyphs like Dhs). */
export const money = (n, currency, decimals) => {
  const code = resolveCode(currency);
  const dp = resolveDecimals(code, decimals);
  return `${money2(n, code, dp)} ${code}`;
};

export function moneyStep(decimals) {
  const dp = resolveDecimals(undefined, decimals);
  return (1 / 10 ** dp).toFixed(dp);
}

/** Prefer company currency over a leftover branch.currency (often still AED). */
export function currencyFromUser(user) {
  const code = String(
    user?.company_currency ||
      user?.company?.currency ||
      user?.default_branch?.currency ||
      "AED",
  ).toUpperCase();
  const decimals = Number(
    user?.currency_decimals ??
      user?.company?.currency_decimals ??
      (code === "OMR" ? 3 : 2),
  );
  return { code, decimals: Number.isFinite(decimals) ? decimals : (code === "OMR" ? 3 : 2) };
}

/** @deprecated Use money() / useCurrency().format — kept as alias for migration. */
export const aed = (n) => money(n, _code, _decimals);

/** Nearest whole currency unit (e.g. 1 AED / 1 OMR). */
export function roundOffToUnit(amount, code) {
  const dp = resolveDecimals(code);
  const factor = 10 ** dp;
  const base = Math.round((Number(amount) || 0) * factor) / factor;
  const delta = Math.round((Math.round(base) - base) * factor) / factor;
  const eps = dp >= 3 ? 0.0005 : 0.005;
  return Math.abs(delta) < eps ? 0 : delta;
}

/** @deprecated Use roundOffToUnit */
export function roundOffToDirham(amount) {
  return roundOffToUnit(amount, _code);
}

export function qty2(n) {
  const x = Math.round((Number(n) || 0) * 100) / 100;
  return Number.isInteger(x) ? String(x) : x.toFixed(2);
}

export const num = (n, decimals) =>
  new Intl.NumberFormat("en", {
    maximumFractionDigits: decimals != null ? decimals : _decimals,
  }).format(Number(n) || 0);

export const pct = (n) => `${Number(n) || 0}%`;
